import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import styles from "./QuestionTypes.module.css";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { validateQuestion } from "../../../../../utils";
import { handleErrorMessage } from "../../../../../redux/actions/classes";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

const TrueFalseQuestion = forwardRef(
  (
    { onSave, onCancel, onMoveUp, onMoveDonw, onDeleteQuestion, initialData = {} },
    ref
  ) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // local state (initialize to neutral values; will be synced from initialData in useEffect)
    const [question, setQuestion] = useState("");
    const [maxMarks, setMaxMarks] = useState("");
    const [isRequired, setIsRequired] = useState(false);
    const [correctAnswer, setCorrectAnswer] = useState(null); // index
    const [options, setOptions] = useState(["TRUE", "FALSE"]);

    const [focusedIndex, setFocusedIndex] = useState(null);
    const containerRef = useRef(null);

    // Helper: preserve incoming choice IDs or generate stable ones
    function generateChoiceId(baseId, index) {
      const src = Array.isArray(initialData.choices)
        ? initialData.choices
        : Array.isArray(initialData.choice)
        ? initialData.choice
        : [];
      if (src[index] && src[index].id) return src[index].id;
      return `tf-${initialData.id ?? Date.now()}-${index}`;
    }

    // Sync incoming initialData into local state (handles legacy keys too)
    useEffect(() => {
      if (!initialData) return;

      // question text
      setQuestion(
        initialData.questionText ??
          initialData.text ??
          initialData.question ??
          ""
      );

      // marks / maxMarks handling (support various key names)
      setMaxMarks(
        initialData.maxMarks ??
          initialData.marks ??
          initialData.max_marks ??
          (typeof initialData.marks === "number" ? String(initialData.marks) : "") ??
          ""
      );

      // required flag (various key names)
      setIsRequired(
        initialData.isMandatory ?? initialData.is_mandatory ?? false
      );

      // incoming choices normalization
      let incomingChoices = [];
      if (Array.isArray(initialData.choices) && initialData.choices.length) {
        incomingChoices = initialData.choices.map(
          (c) => c.choiceText ?? c.choice_text ?? c.text ?? ""
        );
      } else if (Array.isArray(initialData.choice) && initialData.choice.length) {
        incomingChoices = initialData.choice.map(
          (c) => c.choice_text ?? c.choiceText ?? c.text ?? ""
        );
      }

      if (incomingChoices.length >= 2) {
        setOptions(incomingChoices);
      } else {
        // Keep default TRUE/FALSE if nothing meaningful present
        setOptions((prev) => (incomingChoices.length === 1 ? [incomingChoices[0], prev[1]] : ["TRUE", "FALSE"]));
      }

      // determine correct answer index from incoming flags
      const sourceChoices = Array.isArray(initialData.choices)
        ? initialData.choices
        : Array.isArray(initialData.choice)
        ? initialData.choice
        : [];

      if (Array.isArray(sourceChoices) && sourceChoices.length) {
        const idx = sourceChoices.findIndex((c) => !!c.isCorrect || !!c.is_correct);
        setCorrectAnswer(idx >= 0 ? idx : null);
      } else {
        // if no flags but options exactly ["TRUE","FALSE"], try to infer from text like "TRUE" with a flag on initialData.correctAnswer
        if (typeof initialData.correctAnswer === "string") {
          const idx = (initialData.correctAnswer.toLowerCase() === "true") ? 0 : (initialData.correctAnswer.toLowerCase() === "false" ? 1 : null);
          setCorrectAnswer(idx);
        } else {
          setCorrectAnswer(initialData.correctAnswer ?? null);
        }
      }
    }, [initialData]);

    // click outside handler for input focus (unchanged)
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (containerRef.current && !containerRef.current.contains(event.target)) {
          setFocusedIndex(null);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    const handleOptionChange = (index, value) => {
      const newOptions = [...options];
      newOptions[index] = value;
      setOptions(newOptions);
    };

    const handleRemoveOption = () => {};
    const handleAddOption = () => {};

    // expose save to parent (ref.save)
    useImperativeHandle(ref, () => ({
      save: handleSave,
    }));

    function handleSave() {
      const errorMessage = validateQuestion({
        question,
        marks: maxMarks,
        options,
        correctAnswer,
        type: "truefalse",
      });

      if (errorMessage) {
        dispatch(handleErrorMessage(errorMessage));
        return null;
      }

      const payload = {
        id: initialData.id ?? `q-${Date.now()}`,
        type: "trueFalse",
        questionText: question?.trim() ?? "",
        choices: options.map((option, index) => ({
          id: generateChoiceId(initialData.id, index),
          choiceText: option,
          isCorrect: correctAnswer === index,
        })),
        isMandatory: !!isRequired,
        maxMarks: parseInt(maxMarks, 10) || 0,
        icon: initialData.icon ?? null,
      };

      return payload;
    }

    return (
      <div className={styles.questionContainer}>
        <div className={styles.questionHeader}>
          <div className={styles.questionType}>
            <div className={styles.trueFalseToggle}>
              <div className={styles.trueFalseToggleCircle}></div>
            </div>
            <span>{t("quiz.trueFalse")}</span>
          </div>

          <div className={styles.questionActions}>
            <button className={styles.actionButton} onClick={onMoveUp} type="button">
              <KeyboardArrowUpIcon />
            </button>
            <button className={styles.actionButton} onClick={onMoveDonw} type="button">
              <KeyboardArrowDownIcon />
            </button>
            <button className={styles.actionButton} onClick={onDeleteQuestion} type="button">
              <DeleteOutlineIcon />
            </button>
            <div className={styles.marksContainer}>
              <span>{t("quiz.maxMarks")}</span>
              <input
                min={"0"}
                type="number"
                className={styles.marksInput}
                value={maxMarks}
                onChange={(e) => setMaxMarks(e.target.value)}
              />
            </div>
            <div className={styles.requiredContainer}>
              <span>{t("quiz.required")}</span>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={isRequired}
                  onChange={(e) => setIsRequired(e.target.checked)}
                />
                <span className={styles.slider}></span>
              </label>
            </div>
          </div>
        </div>

        <div className={styles.questionContent}>
          <input
            type="text"
            className={styles.questionInput}
            placeholder={t("quiz.question")}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
          />

          <div className={styles.optionsContainer} ref={containerRef}>
            {options.map((option, index) => (
              <div key={index} className={styles.optionItem} tabIndex={-1}>
                <div className={styles.circle}></div>

                <div
                  style={{ display: "flex", width: "100%" }}
                  onMouseDown={() => setFocusedIndex(index)}
                >
                  <input
                   type="text"
                   className={`${styles.optionInput} ${
                     focusedIndex === index ? styles.focusedInputField : ""
                   }`}
                   value={option}
                   readOnly
                 />

                  {focusedIndex === index ? (
                    <div className={styles.optionActions}>
                      <button
                        className={styles.removeOptionButton}
                        onClick={() => handleRemoveOption(index)}
                        type="button"
                      >
                        <CloseIcon fontSize="small" />
                      </button>

                      <label className={styles.correctCheckbox}>
                        <input
                          type="checkbox"
                          checked={correctAnswer === index}
                          onChange={() =>
                            setCorrectAnswer((prev) => (prev === index ? null : index))
                          }
                        />
                        <span>{t("quiz.correctAnswer")}</span>
                      </label>
                    </div>
                  ) : correctAnswer === index ? (
                    <div className={styles.tickMarkOnly}>
                      <CheckIcon />
                    </div>
                  ) : null}
                </div>
              </div>
            ))}

            {/* No add option button for True/False */}
          </div>
        </div>
      </div>
    );
  }
);

export default TrueFalseQuestion;
